import styled from 'styled-components';
import { formatPhone } from 'utils/formatPhone';
import OtpInput from 'react-otp-input';
import { useEffect, useState } from 'react';
import IcBackArrow from 'features/coupon/new-customer/ReferralPage/IcBackArrow';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import close_icon from '../ModalVerifyOTP/close.svg';
import two_step_verification from '../ModalVerifyOTP/two-step-verification.png';
import { Col } from 'components/Box/Col';

interface VerifyOtpFormProps {
  phoneNumber: string;
  error?: string;
  onVerify?: (otp: string) => void;
  onResend?: () => void;
  onClose?: () => void;
}

const VerifyOtpForm = ({ phoneNumber, error, onVerify, onResend, onClose }: VerifyOtpFormProps) => {
  const [code, setCode] = useState('');
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0 && !canResend) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (code: string) => {
    setCode(code);
    if (code.length === 6) {
      onVerify?.(code);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleResend = () => {
    setTimer(60);
    setCanResend(false);
    onResend?.();
    setCode('');
  };

  const handleClose = () => {
    onClose?.();
    setCode('');
    setCanResend(false);
    setTimer(60);
  };

  return (
    <Container>
      <HeaderStyled>
        {isDesktop ? (
          <Col width='100%' alignItems='start' gap='1'>
            <button onClick={handleBack} aria-label='Back'>
              <IcBackArrow width={32} height={32} />
            </button>
            <Title>OTP Verification</Title>
          </Col>
        ) : (
          <>
            <Title>OTP</Title>
            <button type='button' onClick={handleClose} className='button-close'>
              <img src={close_icon} alt='close' />
            </button>
          </>
        )}
      </HeaderStyled>
      {!isDesktop &&
      <ImageBanner src={two_step_verification} alt='two-step-verification' />
      }
      <ContentStyled>
        <p>We have sent a 6-digit OTP</p>
        <p>verification code to</p>
        <p className='phone-number'>{formatPhone(phoneNumber)}</p>
      </ContentStyled>
      <OtpInputStyled>
        <OtpInput
          value={code}
          onChange={handleChange}
          numInputs={6}
          shouldAutoFocus={true}
          inputType='tel'
          containerStyle={{ gap: 8 }}
          renderInput={(props) => <input {...props} placeholder='*' className='otp-input' />}
        />
        {error && <p className='error'>{error}</p>}
      </OtpInputStyled>

      <button className={`resend-button ${canResend ? '' : 'disabled'}`} onClick={handleResend} disabled={!canResend}>
        <p>Resend SMS</p>
      </button>

      <TimerText>
        <span>{canResend ? '' : `Resend code in ${timer}s`}</span>
      </TimerText>
    </Container>
  );
};

export default VerifyOtpForm;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 40px;

  .resend-button {
    width: 100%;
    height: 56px;
    justify-content: center;
    align-items: center;
    background-color: #512dcb;
    border-radius: 100px;
    margin-bottom: 16px;
    p {
      color: #ffffff;
      text-align: center;
      font-family: Poppins;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  @media (max-width: 768px) {
    padding: 0;

    .resend-button {
      height: 48px;

      p {
        font-size: 14px;
      }
    }
  }
`;

const Title = styled.div`
  color: #1d2129;
  text-align: left;
  font-family: Poppins;
  font-size: 28px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.48px;
  width: 100%;

  @media (max-width: 768px) {
    font-size: 24px;
    text-align: center;
  }
`;

const HeaderStyled = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: 16px;

  .button-close {
    position: absolute;
    top: 0;
    right: 0;
    cursor: pointer;
  }

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }
`;

const ContentStyled = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 24px;
  p {
    color: #232f3e;
    text-align: center;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    letter-spacing: 0.28px;
    &.phone-number {
      color: #1d2129;
      font-size: 20px;
      font-weight: 600;
      letter-spacing: 0.36px;
    }
  }

  @media (max-width: 768px) {
    align-items: center;
    p {
      width: 100%;
      font-size: 16px;

      &.phone-number {
        font-size: 16px;
      }
    }
  }
`;

const OtpInputStyled = styled.div`
  align-self: stretch;
  gap: 8px;
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;

  .otp-input {
    flex: 1;
    display: flex;
    height: 50px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex: 1 0 0;
    border-radius: 5px;
    border: 1px solid #C9CDD4;
    background: #FFF;
    outline: none;
    color: #1D2129;
    text-align: center;
    font-family: "GT Walsheim Trial";
    font-size: 30px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    &:active, &:focus, &:focus-visible {
      border-radius: 5px;
      border: 1px solid #EF505F;
      background: #FFF;
      box-shadow: 0px 6px 12px -4px rgba(0, 107, 255, 0.20);
    }
  }
  p.error {
    color: #EF505F;
    text-align: center;
    font-family: Blinker;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 137.5%; /* 24.75px */
    letter-spacing: 0.36px;
    text-transform: capitalize;
  }

  @media (max-width: 768px) {
    .otp-input {
      font-size: 24px;
    }
`;

const TimerText = styled.div`
  display: flex;
  height: 32px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  margin-bottom: 16px;
  span {
    color: #787d89;
    text-align: center;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 28px; /* 175% */
    letter-spacing: 0.32px;
  }

  @media (max-width: 768px) {
    span {
      font-size: 14px;
    }
  }
`;


const ImageBanner = styled.img`
  width: 124px;
  height: 124px;
  aspect-ratio: 1/1;
`;